'use client';

import React from 'react';
import iconRegistry from '../../lib/icons/IconRegistry.js';

/**
 * Componente Icon dinámico que utiliza el IconRegistry
 * Soporta iconos predefinidos, personalizados y configuración dinámica
 */
function Icon({ 
  name, 
  size = 16, 
  color = 'currentColor',
  strokeWidth = 2,
  className = '',
  style = {},
  viewBox = '0 0 24 24',
  fill = 'none',
  stroke = 'currentColor',
  strokeLinecap = 'round',
  strokeLinejoin = 'round',
  ...props 
}) {
  // Obtener el path del icono desde el registro
  const iconPath = iconRegistry.get(name);
  
  // Si no se encuentra el icono, mostrar un icono de placeholder
  if (!iconPath) {
    console.warn(`Icon "${name}" not found in registry`);
    
    // Icono de placeholder (cuadrado con interrogación)
    const placeholderPath = "M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3 M12 17h.01";
    
    return (
      <svg
        width={size}
        height={size}
        viewBox={viewBox}
        fill={fill}
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap={strokeLinecap}
        strokeLinejoin={strokeLinejoin}
        className={`icon icon--placeholder ${className}`}
        style={{ color, ...style }}
        role="img"
        aria-label={`Icon ${name} (not found)`}
        {...props}
      >
        <path d={placeholderPath} />
      </svg>
    );
  }

  // Verificar si hay configuración específica para este icono
  const customViewBox = iconRegistry.get(`${name}_viewBox`) || viewBox;
  const customStrokeWidth = iconRegistry.get(`${name}_strokeWidth`) || strokeWidth;

  return (
    <svg
      width={size}
      height={size}
      viewBox={customViewBox}
      fill={fill}
      stroke={stroke}
      strokeWidth={customStrokeWidth}
      strokeLinecap={strokeLinecap}
      strokeLinejoin={strokeLinejoin}
      className={`icon icon--${name} ${className}`}
      style={{ color, ...style }}
      role="img"
      aria-label={`${name} icon`}
      {...props}
    >
      <path d={iconPath} />
    </svg>
  );
}

/**
 * Componente IconButton que combina Icon con funcionalidad de botón
 */
function IconButton({ 
  icon, 
  onClick, 
  disabled = false,
  size = 16,
  className = '',
  title,
  ariaLabel,
  ...props 
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`icon-button ${className}`}
      title={title || ariaLabel}
      aria-label={ariaLabel || title}
      {...props}
    >
      <Icon name={icon} size={size} />
    </button>
  );
}

/**
 * Componente IconWithText que combina Icon con texto
 */
function IconWithText({ 
  icon, 
  children, 
  iconPosition = 'left',
  gap = 8,
  iconSize = 16,
  className = '',
  ...props 
}) {
  const iconElement = <Icon name={icon} size={iconSize} />;
  
  return (
    <span 
      className={`icon-with-text icon-with-text--${iconPosition} ${className}`}
      style={{ gap: `${gap}px` }}
      {...props}
    >
      {iconPosition === 'left' && iconElement}
      <span className="icon-with-text__content">{children}</span>
      {iconPosition === 'right' && iconElement}
    </span>
  );
}

/**
 * Hook para usar iconos dinámicamente
 */
export function useIcon(name) {
  const iconPath = iconRegistry.get(name);
  const category = iconRegistry.getCategory(name);
  const exists = iconRegistry.has(name);
  
  return {
    path: iconPath,
    category,
    exists,
    render: (props = {}) => <Icon name={name} {...props} />
  };
}

/**
 * Hook para buscar iconos
 */
export function useIconSearch(term) {
  const [results, setResults] = React.useState([]);
  
  React.useEffect(() => {
    if (term && term.length > 0) {
      const searchResults = iconRegistry.search(term);
      setResults(searchResults);
    } else {
      setResults([]);
    }
  }, [term]);
  
  return results;
}

/**
 * Hook para obtener iconos por categoría
 */
export function useIconsByCategory(category) {
  const [icons, setIcons] = React.useState([]);
  
  React.useEffect(() => {
    if (category) {
      const categoryIcons = iconRegistry.getByCategory(category);
      setIcons(categoryIcons);
    } else {
      setIcons([]);
    }
  }, [category]);
  
  return icons;
}

/**
 * Componente IconPicker para seleccionar iconos
 */
export function IconPicker({ 
  onSelect, 
  selectedIcon = null,
  showSearch = true,
  showCategories = true,
  className = '',
  maxHeight = 300
}) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedCategory, setSelectedCategory] = React.useState('all');
  
  const searchResults = useIconSearch(searchTerm);
  const categories = iconRegistry.getCategories();
  
  // Obtener iconos a mostrar
  const iconsToShow = React.useMemo(() => {
    if (searchTerm) {
      return searchResults;
    }
    
    if (selectedCategory === 'all') {
      return iconRegistry.getAll();
    }
    
    return iconRegistry.getByCategory(selectedCategory);
  }, [searchTerm, selectedCategory, searchResults]);

  return (
    <div className={`icon-picker ${className}`}>
      {showSearch && (
        <div className="icon-picker__search">
          <input
            type="text"
            placeholder="Buscar iconos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="icon-picker__search-input"
          />
        </div>
      )}
      
      {showCategories && !searchTerm && (
        <div className="icon-picker__categories">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="icon-picker__category-select"
          >
            <option value="all">Todas las categorías</option>
            {categories.map(category => (
              <option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </option>
            ))}
          </select>
        </div>
      )}
      
      <div 
        className="icon-picker__grid"
        style={{ maxHeight: `${maxHeight}px` }}
      >
        {iconsToShow.map(iconName => (
          <button
            key={iconName}
            type="button"
            className={`icon-picker__item ${selectedIcon === iconName ? 'icon-picker__item--selected' : ''}`}
            onClick={() => onSelect?.(iconName)}
            title={iconName}
          >
            <Icon name={iconName} size={20} />
            <span className="icon-picker__item-name">{iconName}</span>
          </button>
        ))}
      </div>
      
      {iconsToShow.length === 0 && (
        <div className="icon-picker__empty">
          No se encontraron iconos
        </div>
      )}
    </div>
  );
}

export default Icon;
export { IconButton, IconWithText };
